import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { useDashboard } from "@/hooks/useDashboard";
import { useInspirationSources } from "@/hooks/useInspirationSources";
import { ContentPipelineStatus } from "@/components/ContentPipelineStatus";
import { OnboardingGenerationStatus } from "@/components/OnboardingGenerationStatus";
import { FirstContentCelebration } from "@/components/FirstContentCelebration";
import { WelcomeDialog } from "@/components/WelcomeDialog";
// DashboardSidebar and DashboardHeader are now in DashboardLayout
import { ProductTour } from "@/components/ProductTour";

import { ContentCard } from "@/components/ContentCard";
import { ProfileStatsCard } from "@/components/ProfileStatsCard";
import PostingGoalCard from "@/components/PostingGoalCard";
import { CompetitorComparison } from "@/components/CompetitorComparison";
import { FeaturedContentIdeas } from "@/components/FeaturedContentIdeas";
import { CorkModernModal } from "@/components/CorkModernModal";
import { OnboardingProgressCard } from "@/components/OnboardingProgressCard";
import { HotInfoCard } from "@/components/HotInfoCard";
import { ContentIdea } from "@/types/dashboard";
import { useInstagramProfiles } from "@/hooks/useInstagramProfiles";
import { useProfileMetrics } from "@/hooks/useProfileMetrics";
import { useTrashManagement } from "@/hooks/useTrashManagement";
import { 
  Sparkles, 
  Copy, 
  Download, 
  RefreshCw, 
  Filter,
  Star,
  TrendingUp,
  Loader2,
  Plus,
  ArrowRight
} from "lucide-react";
import { useLanguage } from '@/contexts/LanguageContext';
import { Progress } from '@/components/ui/progress';
import { DashboardSkeleton } from '@/components/skeletons/DashboardSkeleton';
import foxDetective from '@/assets/fox-detective.webp';
import foxScenario from '@/assets/fox-scenario-creator.webp';

const Dashboard = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showFirstContentCelebration, setShowFirstContentCelebration] = useState(false);
  const [hasSeenFirstContent, setHasSeenFirstContent] = useState(() => {
    return localStorage.getItem('has_seen_first_content') === 'true';
  });
  const [selectedIdea, setSelectedIdea] = useState<ContentIdea | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  
  const {
    isLoading,
    isRefreshing,
    filteredIdeas,
    error,
    lastRefresh,
    newContentCount,
    filters,
    updateFilters,
    copyToClipboard,
    copyAllScripts,
    exportToCSV,
    refresh
  } = useDashboard(user?.id || null);

  const {
    sources,
    getGenerationStatus,
    generationProgress,
    isLoading: isSourcesLoading
  } = useInspirationSources(user?.id || null);

  const { profiles, hasUsername, isLoading: isProfilesLoading } = useInstagramProfiles(user?.id || null);
  const { metrics: profileMetrics, isLoading: isMetricsLoading } = useProfileMetrics(profiles[0]?.username || null);
  const { moveToTrash } = useTrashManagement();

  // Stabilize initialization - wait for all critical data to load before showing content
  useEffect(() => {
    if (!isLoading && !isProfilesLoading && !isSourcesLoading) {
      // Add small delay to prevent flash from cascading state updates
      const timer = setTimeout(() => {
        setIsInitialized(true);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isLoading, isProfilesLoading, isSourcesLoading]);

  const handleTrashAction = async (id: string, source: 'scraped' | 'user_generated') => {
    const success = await moveToTrash(id, source);
    if (success) {
      refresh();
    }
  };
  
  // Weekly posts change - use the same source as ProfileStatsCard (from Monday of current week)
  // This ensures both the progress bar and the posts card show the same value

  // Get top 3 ideas by viral score - prioritize inspiration ideas, fill with generated if needed
  const { displayedIdeas, hasInspirationIdeas } = useMemo(() => {
    const availableIdeas = filteredIdeas.filter(idea => !idea.is_favorited);
    
    // Separate inspiration and generated ideas
    const inspirationIdeas = availableIdeas.filter(idea => idea.source !== 'user_generated');
    const generatedIdeas = availableIdeas.filter(idea => idea.source === 'user_generated');
    
    // Sort both by viral score
    const sortedInspiration = inspirationIdeas.sort((a, b) => (b.viral_score || 0) - (a.viral_score || 0));
    const sortedGenerated = generatedIdeas.sort((a, b) => (b.viral_score || 0) - (a.viral_score || 0));
    
    // Take best inspiration ideas first, then fill with generated
    const result: typeof availableIdeas = [];
    result.push(...sortedInspiration.slice(0, 3));
    
    // If we have less than 3, fill with generated
    if (result.length < 3) {
      result.push(...sortedGenerated.slice(0, 3 - result.length));
    }
    
    return {
      displayedIdeas: result,
      hasInspirationIdeas: sortedInspiration.length > 0
    };
  }, [filteredIdeas]);

  /**
   * Handle first content celebration
   */
  const handleCelebrationComplete = () => {
    setShowFirstContentCelebration(false);
    setHasSeenFirstContent(true);
    localStorage.setItem('has_seen_first_content', 'true');
  };

  /**
   * Handle generate more content
   */
  const handleGenerateMore = () => {
    navigate('/inspiracje');
  };

  const handleIdeaClick = (idea: ContentIdea) => {
    setSelectedIdea(idea);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedIdea(null);
  };

  // Keep selectedIdea in sync with refreshed data
  useEffect(() => {
    if (selectedIdea && filteredIdeas.length > 0) {
      const updated = filteredIdeas.find(idea => idea.id === selectedIdea.id);
      if (updated && (updated.is_favorited !== selectedIdea.is_favorited || updated.is_used !== selectedIdea.is_used)) {
        setSelectedIdea(updated);
      }
    }
  }, [filteredIdeas, selectedIdea]);

  // First content celebration disabled - was showing on every device change
  // useEffect(() => {
  //   if (!hasSeenFirstContent && filteredIdeas.length > 0 && !isLoading) {
  //     setShowFirstContentCelebration(true);
  //   }
  // }, [hasSeenFirstContent, filteredIdeas.length, isLoading]);

  // Show skeleton loading state to prevent flash of empty content during navigation
  if (!isInitialized) {
    return <DashboardSkeleton />;
  }

  // Error State
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center space-y-6">
            <div className="w-16 h-16 mx-auto bg-destructive rounded-full flex items-center justify-center">
              <RefreshCw className="h-8 w-8 text-destructive-foreground" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
                {t('dashboard.failedToLoad')}
              </h1>
              <p className="text-xl text-muted-foreground mb-6">
                {error}
              </p>
              <Button onClick={refresh} size="lg">
                <RefreshCw className="mr-2 h-5 w-5" />
                {t('common.tryAgain')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Empty State - Only show special states when profile is incomplete
  const hasInstagramProfile = hasUsername;
  const hasAnySources = sources.length > 0;
  const generationStatus = getGenerationStatus();
  const progress = generationProgress.total > 0 
    ? Math.round((generationProgress.completed / generationProgress.total) * 100)
    : 0;

  // Show "complete profile" state only when no Instagram profile
  if (!hasInstagramProfile) {
    return (
      <main className="flex-1 p-3 md:p-6 overflow-y-auto">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="text-center space-y-6 sm:space-y-8 py-8 sm:py-12 px-4">
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground mb-3 sm:mb-4">
                Uzupełnij swój profil
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto mb-6">
                Do poprawnego działania serwisu musisz podać nazwę swojego konta
              </p>
            </div>
            <div className="flex justify-center my-8">
              <img src={foxDetective} alt="Fox mascot" className="w-64 h-64 sm:w-80 sm:h-80 object-contain" />
            </div>
            <Button onClick={() => navigate('/ustawienia')} size="lg" className="w-full sm:w-auto">
              Uzupełnij
            </Button>
          </div>
        </div>
      </main>
    );
  }

  // Show "analyzing" state only when sources exist and generation is in progress
  if (filteredIdeas.length === 0 && hasAnySources && generationStatus.isGenerating) {
    return (
      <main className="flex-1 p-3 md:p-6 overflow-y-auto">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="text-center space-y-6 sm:space-y-8 py-8 sm:py-12 px-4">
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground mb-3 sm:mb-4">
                Analizujemy trendy i inspiracje.
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto mb-6">
                Powiadomimy Cię, gdy wszystko będzie gotowe. W tym czasie:
              </p>
            </div>
            <div className="max-w-md mx-auto space-y-2">
              <div className="text-center">
                <span className="text-2xl font-bold text-primary">{progress}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
            <div className="flex justify-center my-8">
              <img src={foxScenario} alt="Fox mascot" className="w-64 h-64 sm:w-80 sm:h-80 object-contain" />
            </div>
            <Button onClick={() => navigate('/kreator-scenariusza')} size="lg" className="w-full sm:w-auto">
              Stwórz pierwszy scenariusz
            </Button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <>
      {/* Product Tour */}
      <ProductTour isLoading={isLoading} />
      
      {/* Welcome Dialog for first-time users */}
      <WelcomeDialog userName={user?.email?.split('@')[0]} />
      
      {/* First Content Celebration */}
      {showFirstContentCelebration && (
        <FirstContentCelebration
          contentCount={filteredIdeas.length}
          onCelebrationComplete={handleCelebrationComplete}
        />
      )}
      
      {/* Content Area */}
      <main className="flex-1 p-3 md:p-6 flex justify-center overflow-y-auto">
        <div className="w-full max-w-[1200px] space-y-3 md:space-y-5">
          
          {/* Onboarding Progress */}
          <OnboardingProgressCard />

            {/* 1. Overall Stats Section */}
            {profiles.length > 0 && (
              <div className="space-y-3 md:space-y-4 animate-fade-in">
                {/* Weekly Progress */}
                <PostingGoalCard
                  current={profileMetrics?.currentWeekPosts ?? 0}
                  goal={3}
                  timePeriod="week"
                />
                
                {/* Profile Stats */}
                <ProfileStatsCard 
                  profile={profiles[0]}
                  metrics={profileMetrics ? {
                    followersChange: profileMetrics.followersChange ?? undefined,
                    postsChange: profileMetrics.postsChange ?? undefined,
                    postsPerWeek: profileMetrics.postsPerWeek ?? undefined,
                    postsGoal: "3"
                  } : undefined}
                />
              </div>
            )}

            {/* Hot Info */}
            <HotInfoCard />

            {/* CTA for users with no content in library - shown prominently between profile and ideas */}
            {filteredIdeas.length === 0 && (
              <button
                onClick={() => navigate('/kreator-scenariusza')}
                className="w-full group relative overflow-hidden rounded-2xl border-2 border-primary/30 bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 p-6 transition-all duration-300 hover:border-primary/50 hover:shadow-xl hover:shadow-primary/10 hover:scale-[1.01] animate-fade-in"
              >
                {/* Animated gradient background */}
                <div className="absolute inset-0 bg-gradient-to-r from-primary/0 via-primary/5 to-primary/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative flex items-center gap-5">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-lg shadow-primary/30 transition-transform group-hover:scale-110">
                    <Sparkles className="h-6 w-6" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-lg font-bold text-foreground mb-0.5">
                      ✨ Stwórz swój pierwszy scenariusz
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Nie czekaj, przygotuj gotowy pomysł na Twój następny film
                    </p>
                  </div>
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md transition-transform group-hover:translate-x-1">
                    <ArrowRight className="h-5 w-5" />
                  </div>
                </div>
              </button>
            )}

            {/* 2. Side-by-Side: Competitor Comparison & Featured Ideas */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-5 pb-16 animate-fade-in items-stretch" style={{ animationDelay: '0.1s' }}>
              {/* Competitor Comparison */}
              <div data-tour="competitors-section" className="order-2 lg:order-1 flex">
                <div className="flex-1">
                  <CompetitorComparison timeRange="week" />
                </div>
              </div>

              {/* Featured Content Ideas */}
              <div data-tour="featured-ideas" className="order-1 lg:order-2 flex">
                <div className="flex-1">
                  <FeaturedContentIdeas
                    ideas={displayedIdeas}
                    onCopyScript={(content) => copyToClipboard(content, 'Script')}
                    onSeeMore={() => navigate(hasInspirationIdeas ? '/biblioteka?tab=best' : '/biblioteka?tab=generated')}
                    onIdeaClick={handleIdeaClick}
                    onFavoriteToggle={refresh}
                    hasInspirationIdeas={hasInspirationIdeas}
                    isWaitingForContent={hasAnySources && displayedIdeas.length === 0}
                    hasNoInspirationSources={!hasAnySources}
                  />
                </div>
              </div>
            </div>

            {/* No Featured Ideas */}
            {displayedIdeas.length === 0 && filteredIdeas.length > 0 && (
              <div className="text-center py-8 glass-card rounded-2xl border border-border/50 animate-scale-in">
                <div className="w-12 h-12 mx-auto bg-gradient-to-br from-primary/20 to-primary/5 rounded-full flex items-center justify-center mb-3">
                  <Star className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-base font-semibold text-foreground mb-2">
                  {t('dashboard.noFeaturedIdeas')}
                </h3>
                <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
                  {t('dashboard.noFeaturedIdeasDesc')}
                </p>
                <Button
                  onClick={() => navigate('/biblioteka')}
                  size="sm"
                  className="hover-scale"
                >
                  {t('dashboard.viewAllIdeas')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            )}

        </div>
      </main>

      {/* Content Detail Modal */}
      <CorkModernModal
        idea={selectedIdea}
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onCopyScript={(content) => copyToClipboard(content, 'Script')}
        onFavoriteToggle={() => {
          refresh();
        }}
        onUsedToggle={() => {
          refresh();
        }}
        onTrash={handleTrashAction}
      />
    </>
  );
};

export default Dashboard;